/** @file shellHandlers: what each shell binding does to the store (one handler per shellBindings id). */
import type { EditorStore, MenuName, PuppetScreen, Tool } from '../state/editorTypes';
import { routePuppet } from './routePuppet';

const TOOLS: Record<string, Tool> = { 'tool.hand': 'hand', 'tool.move': 'move', 'tool.rotate': 'rotate', 'tool.scale': 'scale', 'tool.ik': 'ik' };

/** Escape: close an open context menu, then a menu-bar menu, then a dialog; on Settings go back to the open puppet (or the projects list). */
function escape(store: EditorStore): void {
  const s = store.getState();
  if (s.menu !== null) { s.closeMenu(); return; }
  if (s.shell.menuBar !== null) { s.setShell({ menuBar: null }); return; }
  if (s.shell.dialog !== null) { s.setShell({ dialog: null }); return; }
  if (s.route.screen !== 'settings') return;
  const id = s.puppet.id;
  s.navigate(id === null ? { screen: 'projects' } : { screen: 'pose', puppetId: id });
}

/** Switches to a puppet screen when a puppet is routed or open. */
const screen = (store: EditorStore, name: PuppetScreen) => (): void => {
  const s = store.getState();
  const id = routePuppet(s.route) ?? s.puppet.id;
  if (id !== null) s.navigate({ screen: name, puppetId: id });
};

/** Alt+letter: opens that menu-bar menu, or closes it when it is the open one. */
const menu = (store: EditorStore, name: MenuName) => (): void => {
  const s = store.getState();
  s.setShell({ menuBar: s.shell.menuBar === name ? null : name });
};

/** Handlers keyed by binding id; they read the store when they run, never at creation. */
export function shellHandlers(store: EditorStore): Record<string, () => void> {
  const tools = Object.fromEntries(Object.entries(TOOLS).map(([id, tool]) => [id, () => { store.getState().setTool(tool); }]));
  return {
    'history.undo': () => { void store.getState().undo(); },
    'history.redo': () => { void store.getState().redo(); },
    'history.redoAlt': () => { void store.getState().redo(); },
    'app.settings': () => { store.getState().navigate({ screen: 'settings' }); },
    'app.escape': () => { escape(store); },
    'menu.file': menu(store, 'file'),
    'menu.edit': menu(store, 'edit'),
    'menu.view': menu(store, 'view'),
    'menu.help': menu(store, 'help'),
    'screen.pose': screen(store, 'pose'),
    'screen.art': screen(store, 'art'),
    'screen.agent': screen(store, 'agent'),
    ...tools,
    'gizmo.space': () => { const s = store.getState(); s.setSpace(s.space === 'local' ? 'world' : 'local'); },
    'gizmo.pivot': () => { const s = store.getState(); s.setPivot(s.pivot === 'pivot' ? 'center' : 'pivot'); },
  };
}
